/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { BEM } from "@adapptio/lib-components";
import { Panel } from "../../../../../ui/components/Panel";
import { ThemeEditorOutlineStyles } from "./ThemeEditorOutlineStyles";
import { useContextMenu, useStateWithEvent } from "../../../../../ui/hooks";
import { ThemeModel } from "../../../Common/ThemeModel";
import { ITreeViewProps } from "../../../../../ui/components/Tree/TreeView";

export interface IThemeEditorOutlineProps {
	className?: string;
	themeModel: ThemeModel;
}

export const ThemeEditorOutline: React.FC<IThemeEditorOutlineProps> = ({ className, themeModel }) => {
	const baseClass = BEM("edt-theme-editor-outline");

	const selection = useMemo(() => themeModel.getSelection(), [ themeModel ]);

	const { showMenu } = useContextMenu("outlineNode");

	const selectedNodes = useStateWithEvent(selection.onChange, () =>
		selection.getItems().map((item) => String(item.nodeId))
	);

	const handleContextMenuClick = useCallback<ITreeViewProps["onNodeContextMenu"]>(
		(treeNode, event) => {
			showMenu(event, { treeNode });
		},
		[ showMenu ]
	);

	if (className) {
		baseClass.add(className);
	}

	return (
		<Panel scroll className={baseClass.toClassName()}>
			<div className={baseClass.element("styles").toClassName()}>
				<ThemeEditorOutlineStyles
					themeModel={themeModel}
					selection={selection}
					selectedNodes={selectedNodes}
					onNodeContextMenu={handleContextMenuClick}
				/>
			</div>
		</Panel>
	);
};
